import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { auction as auctionApi } from '../services/api';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles, Package } from 'lucide-react';

/**
 * Landing page for Stripe's success redirect. Reads session_id from the URL
 * and calls /verify to finalize the auction payment. Idempotent — safe to
 * refresh.
 */
export default function AuctionCheckoutSuccess() {
  const { aid } = useParams();
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [state, setState] = useState('verifying'); // verifying | success | error
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');
  const [handoff, setHandoff] = useState(null);

  useEffect(() => {
    if (!sessionId) { setErr('Missing session id'); setState('error'); return; }
    auctionApi.checkoutVerify(aid, sessionId)
      .then(r => { setResult(r); setState('success'); return auctionApi.checkoutState(aid).then(setHandoff).catch(() => {}); })
      .catch(e => { setErr(e?.response?.data?.detail || 'Verification failed'); setState('error'); });
  }, [aid, sessionId]);

  if (state === 'verifying') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="pbb-card p-8 text-center max-w-md w-full" data-testid="checkout-verifying">
          <Loader2 className="mx-auto animate-spin text-green-400" size={28} />
          <div className="mt-3 font-semibold">Confirming your payment…</div>
          <p className="text-xs text-gray-500 mt-1">This takes about a second.</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <div className="pbb-card p-8 text-center max-w-md w-full" data-testid="checkout-verify-error">
          <XCircle className="mx-auto text-red-400" size={30} />
          <div className="mt-3 font-semibold">We couldn't confirm your payment</div>
          <p className="text-sm text-gray-400 mt-1">{err}</p>
          <p className="text-xs text-gray-500 mt-3">If money did leave your account, contact support — you won't be double-charged.</p>
          <Link to={`/auction/${aid}/checkout`} className="pbb-btn-green mt-5 inline-block max-w-xs">Back to checkout</Link>
        </div>
      </div>
    );
  }

  const paidUsd = result?.amountUsd || 0;
  const capital = result?.allocation === 'capital';
  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="pbb-card p-6 text-center max-w-md w-full space-y-4" data-testid="checkout-verified">
        <CheckCircle2 className="mx-auto text-green-400" size={40} />
        <div>
          <div className="text-xl font-semibold">Payment complete!</div>
          <div className="text-sm text-gray-400 mt-1">Thank you for supporting the movement.</div>
        </div>
        <div className="rounded-lg bg-green-500/5 border border-green-500/30 p-4">
          <div className="text-[11px] uppercase tracking-widest text-green-400">You just gave</div>
          <div className="mt-1 text-3xl font-black tabular-nums" data-testid="checkout-paid-amount">${paidUsd.toFixed(2)}</div>
          <div className="mt-2 text-xs text-gray-300 inline-flex items-center gap-1 justify-center">
            <Sparkles size={12} className="text-amber-300" />
            100% routed to {capital ? 'the Capital Fund' : 'the Community + Youth funds (50/50)'}
          </div>
        </div>
        {handoff?.pickupInstructions && (
          <div className="rounded-lg border border-[#2a2a2a] bg-black/40 p-4 text-left" data-testid="checkout-handoff">
            <div className="text-[11px] uppercase tracking-widest text-gray-300 inline-flex items-center gap-1.5"><Package size={12}/> Delivery / pickup instructions</div>
            <p className="mt-2 text-sm text-gray-100 whitespace-pre-line" data-testid="checkout-handoff-text">{handoff.pickupInstructions}</p>
            <div className="mt-3 text-xs text-gray-300">Handoff code: <span className="font-mono font-bold text-white" data-testid="checkout-handoff-code">{handoff.confirmationCode}</span> · share it only when you receive the item.</div>
            <div className="mt-1 text-[11px] text-gray-400">We also sent these to your email and phone.</div>
          </div>
        )}
        <Link to="/auction" className="pbb-btn-green inline-flex items-center gap-1 max-w-xs mx-auto" data-testid="checkout-back-btn">
          Back to auctions <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
